import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { TimeoffEmployeeRootPageDocument } from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import type { RouteContext } from 'modules/employees/lib/types'
import type Employee from 'modules/employees/store/models/Employee'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import TimeoffCard from 'modules/timeoff/components/Card'
import CollectionsBootstrap from 'modules/timeoff/lib/EmployeesCollectionsBootstrap'
import { LeavesContextProvider } from 'modules/timeoff/lib/LeavesContextProvider'
import { ContractsVersionProvider } from 'modules/timeoff/lib/contractsVersionContext'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'
import allowanceIncidences from 'modules/timeoff/store/collections/allowanceIncidences'
import allowances from 'modules/timeoff/store/collections/allowances'
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import medicalLeaveUpdates from 'modules/timeoff/store/collections/medicalLeaveUpdates'
import policies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'

const Container = () => {
  const { navigation } = useNavigation()
  const { employee } = useOutletContext<RouteContext>()
  const navigate = useNavigate()

  // Essential data loading
  const { loading: essentialDataLoading } = useAsyncData(
    () => (employee ? fetchEssentialData(employee) : Promise.resolve()),
    [employee?.id]
  )

  // Secondary data loading
  const { loading: secondaryDataLoading } = useAsyncData(
    () => (employee ? fetchSecondaryData(employee) : Promise.resolve()),
    [employee?.id]
  )

  const {
    data,
    loading: graphqlLoading,
    error,
  } = useQuery(TimeoffEmployeeRootPageDocument, {
    variables: {
      employeeId: employee.id,
      featureId: Features.timeoff_manual_adjustment_options,
    },
  })

  const readLeaves = !!data?.employees.employee?._policies?.readLeaves.can

  if (!!employee && !readLeaves && !graphqlLoading && !essentialDataLoading) {
    navigate(navigation.employees.id.fullPath({ id: employee.id }))
    return null
  }

  const canManageTimeoff = !!data?.timeoff?._policies.companyManageTimeoff.can
  const canCreateLeaves = !!data?.employees.employee?._policies?.createLeaves.can
  const canApproveLeaves = !!data?.employees.employee?._policies?.approveLeaves.can
  const hasManualAdjustmentOptions =
    !!data?.companies.featureAvailabilitiesConnection.nodes[0]?.available

  if (graphqlLoading || essentialDataLoading) return <Loading />

  if (error) {
    return (
      <ResponsivePage.Body variant="full" showHeader={false}>
        <Box padding="s0">
          <ErrorBoundary />
        </Box>
      </ResponsivePage.Body>
    )
  }

  if (!employee || !readLeaves) {
    return (
      <ResponsivePage.Body variant="full" showHeader={false}>
        <Box padding="s0">
          <ErrorBoundary blankSlate />
        </Box>
      </ResponsivePage.Body>
    )
  }

  return (
    <ContractsVersionProvider employeeId={employee?.id ?? 0}>
      <LeavesContextProvider>
        <ResponsivePage.Body variant="full" showHeader={false}>
          <TimeoffCard
            employee={employee ?? {}}
            canManageTimeoff={canManageTimeoff}
            canCreateLeaves={canCreateLeaves}
            canApproveLeaves={canApproveLeaves}
            hasManualAdjustmentOptions={hasManualAdjustmentOptions}
            allowances={allowances}
            allowanceIncidences={allowanceIncidences}
            policies={policies}
            policyTimelines={policyTimelines}
            loading={secondaryDataLoading}
          >
            <Outlet />
          </TimeoffCard>
        </ResponsivePage.Body>
      </LeavesContextProvider>
    </ContractsVersionProvider>
  )
}

// Function to load essential data
const fetchEssentialData = (employee: Employee) => {
  const employeeId = employee.attributes.id
  const allowancesData = hasPolicyTemporality()
    ? {}
    : { timeoff_policy_id: employee.attributes.timeoff_policy_id }

  const essentialCollections = [
    {
      target: allowances,
      data: allowancesData,
    },
    {
      target: policies,
      data: {
        employee_id: employeeId,
      },
    },
    {
      target: policyTimelines,
      data: {
        employee_id: employeeId,
      },
    },
  ]

  return Promise.all(
    essentialCollections.map((info) =>
      info.target.fetch({
        data: info.data,
      })
    )
  )
}

// Function to load secondary data
const fetchSecondaryData = (employee: Employee) => {
  const employeeId = employee.attributes.id

  const secondaryCollections = [
    {
      target: allowanceIncidences,
      data: {
        employee_ids: [employeeId],
      },
    },
    {
      target: companyHolidays,
      data: employee.attributes.location_id ? { location_id: employee.attributes.location_id } : {},
    },
    {
      target: leaveTypes,
      data: {},
    },
    {
      target: medicalLeaveUpdates,
      data: {
        employee_id: employeeId,
      },
    },
  ]

  return Promise.all(
    secondaryCollections.map((info) =>
      info.target.fetch({
        data: info.data,
      })
    )
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
